import { Box, Text } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useProjectsDataTable } from 'generated/resources/datatable.graphql'
import compact from 'lodash/compact'

import { formatBackendDateToDayMonthYear } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { formatProjectHours } from 'modules/project_management/lib/formatHours'
import {
  getFixedCostPlanningProgressBarLabel,
  getFixedTimeBudgetProgressBarLabel,
} from 'modules/project_management/lib/formatText'

import { Amount } from '../../Amount'
import { BudgetProgressBar, EInfoLabelLocation } from '../../BudgetProgressBar'
import BillableTag from '../../labelTags/BillableTag'
import ProjectStatusTag from '../../labelTags/StatusTag'
import ActionButtons from './TableBody/ActionButtons'
import ProjectWorkers from './TableBody/ProjectWorkers'

type ProjectsDataTableProjection = Parameters<typeof useProjectsDataTable>[0][number]

const ProjectNameProjection = (): ProjectsDataTableProjection => {
  return {
    kind: ProjectionKind.Custom,
    identifier: 'name',
    title: i18n.t('project_management.table.projects_list.headers.name'),
    template: '{{name}}',
    width: 200,
    colSpan: 3,
    sortBy: 'name',
    render: ({ name }) => {
      return (
        <Box minWidth="s200">
          <Text.Normal overflowWrap>{name}</Text.Normal>
        </Box>
      )
    },
  }
}

const ProjectCodeProjection = (): ProjectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'code',
  title: i18n.t('project_management.table.projects_list.headers.code'),
  template: '{{code}}',
  width: 120,
  colSpan: 3,
  sortBy: 'code',
  render: ({ code }) => {
    return (
      <Box minWidth="s120">
        <Text.Normal overflowWrap>{code || '-'}</Text.Normal>
      </Box>
    )
  },
})

const ProjectWorkersProjection = (): ProjectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'projectWorkers',
  title: i18n.t('project_management.table.projects_list.headers.employees'),
  render: ({ projectworkersConnection }) => {
    const totalCount = projectworkersConnection.totalCount
    const employees = compact(projectworkersConnection?.edges.map((edge) => edge.node.employee))

    return totalCount && employees.length ? (
      <ProjectWorkers workers={employees} totalCount={totalCount} />
    ) : (
      '-'
    )
  },
  template: '{{projectWorkers}}',
  colSpan: 3,
})

const ProjectClientsProjection = (): ProjectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'projectClients',
  title: i18n.t('project_management.table.projects_list.headers.client'),
  template: '{{client}}',
  colSpan: 3,

  render: ({ client }) => {
    return (
      <Box minWidth="s120">
        <Text.Normal overflowWrap>{client?.legalName || '-'}</Text.Normal>
      </Box>
    )
  },
})

const ProjectHoursProjection = ({
  hasFixedTimeBudget,
}: {
  hasFixedTimeBudget: boolean
}): ProjectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'hours',
  title: i18n.t('project_management.table.projects_list.headers.hours'),
  render: ({ inputedMinutes, fixedTimeBudget }) => (
    <Box minWidth="s80">
      {hasFixedTimeBudget && fixedTimeBudget?.budgetAllocation ? (
        <Box width="s140">
          <BudgetProgressBar
            currentAmount={inputedMinutes || 0}
            totalAmount={fixedTimeBudget?.budgetAllocation}
            infoLabelLocation={EInfoLabelLocation.TOOLTIP}
            infoLabel={getFixedTimeBudgetProgressBarLabel(
              formatProjectHours(inputedMinutes),
              formatProjectHours(fixedTimeBudget?.budgetAllocation)
            )}
          />
        </Box>
      ) : (
        <Text.Normal>{formatProjectHours(inputedMinutes)}</Text.Normal>
      )}
    </Box>
  ),
  template: '{{hours}}',
  colSpan: 3,
})

const ProjectStartDateProjection = (): ProjectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  title: i18n.t('project_management.table.projects_list.headers.startDate'),
  identifier: 'startDate',
  template: '{{startDate}}',
  render: ({ startDate }) => (
    <Box minWidth="s120">
      <Text.Normal>{(startDate && formatBackendDateToDayMonthYear(startDate)) || '-'}</Text.Normal>
    </Box>
  ),
  width: 120,
  colSpan: 3,
  sortBy: 'startDate',
})

const ProjectDueDateProjection = (): ProjectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  title: i18n.t('project_management.table.projects_list.headers.dueDate'),
  identifier: 'dueDate',
  template: '{{dueDate}}',
  render: ({ dueDate }) => (
    <Box minWidth="s120">
      <Text.Normal>{(dueDate && formatBackendDateToDayMonthYear(dueDate)) || '-'}</Text.Normal>
    </Box>
  ),
  width: 120,
  colSpan: 3,
  sortBy: 'dueDate',
})

const ProjectIsBillableProjection = (): ProjectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'isBillable',
  title: i18n.t('project_management.table.projects_list.headers.billing'),
  render: ({ isBillable }) => (
    <Box minWidth="s120">
      <BillableTag isBillable={!!isBillable} />
    </Box>
  ),
  template: '{{isBillable}}',
  sortBy: 'isBillable',
  colSpan: 3,
})

const ProjectCostProjection = (locale: string): ProjectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'cost',
  title: i18n.t('project_management.table.projects_list.headers.cost'),
  render: ({
    fixedCostCents,
    laborCostCents,
    fixedCostPlanning,
    legalEntity_currency,
    spendingCostCents,
  }) => {
    const currency = legalEntity_currency
    const cost =
      (Number(fixedCostCents) || 0) +
      (Number(laborCostCents) || 0) +
      (Number(spendingCostCents) || 0)

    return (
      <Box minWidth="s120">
        {fixedCostPlanning?.budgetAllocationCents ? (
          <Box width="s140">
            <BudgetProgressBar
              currentAmount={cost}
              totalAmount={fixedCostPlanning.budgetAllocationCents}
              infoLabelLocation={EInfoLabelLocation.TOOLTIP}
              infoLabel={getFixedCostPlanningProgressBarLabel(
                cost,
                fixedCostPlanning.budgetAllocationCents,
                currency,
                locale
              )}
            />
          </Box>
        ) : (
          <Amount amount={cost} currency={currency} />
        )}
      </Box>
    )
  },
  template: '{{cost}}',
  colSpan: 3,
})

const ProjectStatusProjection = (): ProjectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'status',
  title: i18n.t('project_management.table.projects_list.headers.status'),
  render: ({ status }) => status && <ProjectStatusTag projectStatus={status} />,
  template: '{{status}}',
  colSpan: 3,
  sortBy: 'status',
})

const ProjectActionsProjection = ({
  canManageProjects,
  canCreateProjects,
  canDeleteProjects,
  hasSubprojectsFeature,
}: {
  canManageProjects: boolean
  canCreateProjects: boolean
  canDeleteProjects: boolean
  hasSubprojectsFeature: boolean
}): ProjectsDataTableProjection => ({
  kind: ProjectionKind.Decorative,
  identifier: 'actions',
  render: ({ id, name, _policies_assignUnassignProjectWorkers_can }) =>
    id &&
    name && (
      <ActionButtons
        projectId={id}
        projectName={name}
        canManageProjects={canManageProjects}
        canCreateProjects={canCreateProjects}
        canDeleteProjects={canDeleteProjects}
        canAssignEmployees={_policies_assignUnassignProjectWorkers_can || false}
        hasSubprojectsFeature={hasSubprojectsFeature}
      />
    ),
  fixed: true,
  sticky: true,
})

export const projectProjections = ({
  locale,
  hasBillable,
  hasFixedTimeBudget,
  hasClients,
  hasCost,
  hasSubprojectsFeature,
  canManageProjects,
  canCreateProjects,
  canDeleteProjects,
}: {
  locale: string
  hasBillable: boolean
  hasFixedTimeBudget: boolean
  hasClients: boolean
  hasCost: boolean
  hasSubprojectsFeature: boolean
  canManageProjects: boolean
  canCreateProjects: boolean
  canDeleteProjects: boolean
}) =>
  compact([
    ProjectNameProjection(),
    ProjectCodeProjection(),
    ProjectWorkersProjection(),
    hasClients ? ProjectClientsProjection() : null,
    ProjectHoursProjection({ hasFixedTimeBudget }),
    hasBillable ? ProjectIsBillableProjection() : null,
    hasCost ? ProjectCostProjection(locale) : null,
    ProjectStartDateProjection(),
    ProjectDueDateProjection(),
    ProjectStatusProjection(),
    ProjectActionsProjection({
      canManageProjects,
      canCreateProjects,
      canDeleteProjects,
      hasSubprojectsFeature,
    }),
  ])
